import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import MainLayout from "./layout/MainLayout";
import Homepage from "./pages/Homepage";
import PropertyManagementSystem from "./pages/products/PropertyManagementSystem";
import ChannelManager from "./pages/products/ChannelManager";
import BookingEngine from "./pages/products/BookingEngine";
import PointOfSale from "./pages/products/PointOfSale";
import ProstayNexus from "./pages/products/ProstayNexus";
import PrivacyPolicy from "./pages/privacy/PrivacyPolicy";
import TermsAndConditions from "./pages/terms/TermsAndConditions";
import CookiesPolicy from "./pages/cookies/CookiesPolicy";
import ContactSupport from "./pages/contact/ContactSupport";
import ContactUs from "./pages/contact/ContactUs";
import Pricing from "./pages/pricing/Pricing";
import DemoRequest from "./pages/pricing/DemoRequest";
import BecomePartner from "./pages/partner/BecomePartner";
import ProductUpdates from "./pages/products/ProductUpdates";
import Resources from "./pages/resources/Resources";
import Category from "./pages/resources/Category";
import ScrollToTop from './ScrollToTop';
import NewsAndPress from "./pages/company/NewsAndPress";
import NewsArticle from "./pages/news-articles/NewsArticle";
import Blog from "./pages/company/Blog";
import './i18n';
import { setLanguage } from "./i18n";
import WhyProstay from "./whyprostay/WhyProstay";
import Article from "./pages/news-articles/Article";
import DataPrivacy from "./pages/DataPrivacy";
import HotelManagementSoftware from "./pages/HotelManagementSoftware";
import { useTranslation } from "react-i18next";
import LanguageGuard from "./pages/LanguageGuard";
import CookieConsent from "./components/CookieConsent";
import ProstayPay from "./pages/products/ProstayPay";
import ToolsDashboard from "./pages/tools/ToolsDashboard";
import AdrCalculator from "./pages/tools/AdrCalculator";
import RevPARCalculator from "./pages/tools/RevPARCalculator";
import OccupancyRate from "./pages/tools/OccupancyRate";
import GopparCalculator from "./pages/tools/GopparCalculator";
import TRevPARCalculator from "./pages/tools/TrevParCalculator";
import TRevPABCalculator from "./pages/tools/TrevPabCalculator";
import CPORCalculator from "./pages/tools/CporCalculator";
import ALOSCalculator from "./pages/tools/AlosCalculator";
import MultiPropertyGroups from "./pages/MultiPropertyGroups";
import VacationRentals from "./pages/VacationRentals";
import BedAndBreakfast from "./pages/BedAndBreakfast";
import ProstayAi from "./pages/products/ProstayAi";
import AiReservationsAgent from "./pages/products/AiReservationsAgent";
import AiFrontOffice from "./pages/products/AiFrontOffice";
import ProstayReview from "./pages/ProstayReview";
import SmallHotels from "./pages/SmallHotels";
import BlogPage from "./pages/company/BlogPage";
import HotelSales from "./pages/HotelSales";
import NotFound from "./pages/NotFound";
import LinenParCalculator from "./pages/LinenParCalculator";
import ProstayAlternatives from "./pages/ProstayAlternatives";
import ProstayCloudbeds from "./pages/ProstayCloudbeds";
import ProstayMews from "./pages/ProstayMews";
import ProstayRoomRaccoon from "./pages/ProstayRoomRaccoon";
import ProstayProtel from "./pages/ProstayProtel";
import ProstayLittlehotelier from "./pages/ProstayLittlehotelier";
import ProstayOpera from "./pages/ProstayOpera";
import CustomerAcquisitionCost from "./pages/CustomerAcquisitionCost";

// Language setter from URL
const AppLanguageSetter = () => {
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.prerenderReady = false;
    }

    // ✅ After short delay (or when content is loaded), mark ready
    const timer = setTimeout(() => {
      window.prerenderReady = true;
    }, 1500); // You can increase if needed, or use a data load hook

    return () => clearTimeout(timer);
  }, []);

  const location = useLocation();

  useEffect(() => {
    const supportedLanguages = ["en", "ro", "es", "de"];
    const defaultLanguage = "en";

    const pathParts = location.pathname.split("/").filter(Boolean);
    const lang = pathParts[0];

    if (supportedLanguages.includes(lang)) {
      setLanguage(lang);
    } else {
      setLanguage(defaultLanguage);
    }
  }, [location.pathname]);

  return null;
};

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <Router>
      <ScrollToTop />
      <AppLanguageSetter />
      <CookieConsent />

      <Routes>
        {/* Default homepage */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Homepage />} />
        </Route>



        {/* Product pages */}
        <Route path="/property-management-system/" element={<MainLayout />}>
          <Route index element={<PropertyManagementSystem />} />
        </Route>
        <Route path="/channel-manager/" element={<MainLayout />}>
          <Route index element={<ChannelManager />} />
        </Route>
        <Route path="/booking-engine/" element={<MainLayout />}>
          <Route index element={<BookingEngine />} />
        </Route>
        <Route path="/ai/" element={<MainLayout />}>
          <Route index element={<ProstayAi />} />
        </Route>
        <Route path="/ai/ai-booking-assistant/" element={<MainLayout />}>
          <Route index element={<AiReservationsAgent />} />
        </Route>
        <Route path="/ai/ai-front-desk-agent/" element={<MainLayout />}>
          <Route index element={<AiFrontOffice />} />
        </Route>
        <Route path="/point-of-sale/" element={<MainLayout />}>
          <Route index element={<PointOfSale />} />
        </Route>
        <Route path="/prostay-nexus/" element={<MainLayout />}>
          <Route index element={<ProstayNexus />} />
        </Route>
        <Route path="/prostay-pay/" element={<MainLayout />}>
          <Route index element={<ProstayPay />} />
        </Route>
        <Route path="/not-found/" element={<MainLayout />}>
          <Route index element={<NotFound />} />
        </Route>

        {/* Other pages */}
        <Route path="/privacy-policy/" element={<MainLayout />}>
          <Route index element={<PrivacyPolicy />} />
        </Route>
        <Route path="/data-privacy/" element={<MainLayout />}>
          <Route index element={<DataPrivacy />} />
        </Route>
        <Route path="/cookies-policy/" element={<MainLayout />}>
          <Route index element={<CookiesPolicy />} />
        </Route>
        <Route path="/terms-and-conditions/" element={<MainLayout />}>
          <Route index element={<TermsAndConditions />} />
        </Route>
        <Route path="/discover-prostay/" element={<MainLayout />}>
          <Route index element={<WhyProstay />} />
        </Route>
        <Route path="/small-hotels/" element={<MainLayout />}>
          <Route index element={<SmallHotels />} />
        </Route>
        <Route path="/support/" element={<MainLayout />}>
          <Route index element={<ContactSupport />} />
        </Route>
        <Route path="/contact-us/" element={<MainLayout />}>
          <Route index element={<ContactUs />} />
        </Route>
        <Route path="/pricing/" element={<MainLayout />}>
          <Route index element={<Pricing />} />
        </Route>
        <Route path="/request-demo/" element={<MainLayout />}>
          <Route index element={<DemoRequest />} />
        </Route>
        <Route path="/become-a-partner/" element={<MainLayout />}>
          <Route index element={<BecomePartner />} />
        </Route>
        <Route path="/product-updates/" element={<MainLayout />}>
          <Route index element={<ProductUpdates />} />
        </Route>
        <Route path="/resources/" element={<MainLayout />}>
          <Route index element={<Resources />} />
        </Route>
        <Route path="/resources/:categoryName/" element={<MainLayout />}>
          <Route index element={<Category />} />
        </Route>
        <Route path="/news/" element={<MainLayout />}>
          <Route index element={<NewsAndPress />} />
        </Route>
        <Route path="/resources/:categoryName/:subCategoryName/:slug/" element={<MainLayout />}>
          <Route index element={<Article />} />
        </Route>
        <Route path="/blog/" element={<MainLayout />}>
          <Route index element={<Blog />} />
        </Route>
        <Route path="/blog/page/:pageNum/" element={<MainLayout />}>
          <Route index element={<Blog />} />
        </Route>
        <Route path="/hotel-management-software/" element={<MainLayout />}>
          <Route index element={<HotelManagementSoftware />} />
        </Route>
        <Route path="/multi-property-groups/" element={<MainLayout />}>
          <Route index element={<MultiPropertyGroups />} />
        </Route>
        <Route path="/vacation-rentals/" element={<MainLayout />}>
          <Route index element={<VacationRentals />} />
        </Route>
        <Route path="/bed-breakfast/" element={<MainLayout />}>
          <Route index element={<BedAndBreakfast />} />
        </Route>
        <Route path="/news/prostay-launches-a-new-standard-in-hotel-management-technology/" element={<MainLayout />}>
          <Route index element={<NewsArticle />} />
        </Route>
        <Route path="/reviews/" element={<MainLayout />}>
          <Route index element={<ProstayReview />} />
        </Route>
        <Route path="/blog/:slug/" element={<MainLayout />}>
          <Route index element={<BlogPage />} />
        </Route>
        <Route path="/blog/:slug/page/:pageNum" element={<MainLayout />}>
          <Route index element={<BlogPage />} />
        </Route>
        <Route path="/resources/:categoryName/:subCategoryName?/" element={<MainLayout />}>
          <Route index element={<Category />} />
        </Route>
        <Route path="/:lang/news/" element={<MainLayout />}>
          <Route index element={<NewsAndPress />} />
        </Route>
        <Route path="/:lang/discover-prostay/" element={<MainLayout />}>
          <Route index element={<WhyProstay />} />
        </Route>
        <Route path="/:lang/blog/" element={<MainLayout />}>
          <Route index element={<Blog />} />
        </Route>
        <Route path="/:lang/blog/page/:pageNum/" element={<MainLayout />}>
          <Route index element={<Blog />} />
        </Route>
        <Route path="/:lang/news/prostay-launches-a-new-standard-in-hotel-management-technology/" element={<MainLayout />}>
          <Route index element={<NewsArticle />} />
        </Route>
        <Route path="/es/" element={<MainLayout />}>
          <Route index element={<Homepage />} />
        </Route>
        <Route path="/:lang/become-a-partner/" element={<MainLayout />}>
          <Route index element={<BecomePartner />} />
        </Route>
        <Route path="/:lang/product-updates/" element={<MainLayout />}>
          <Route index element={<ProductUpdates />} />
        </Route>
        <Route path="/:lang/pricing/" element={<MainLayout />}>
          <Route index element={<Pricing />} />
        </Route>
        <Route path="/:lang/request-demo/" element={<MainLayout />}>
          <Route index element={<DemoRequest />} />
        </Route>

        <Route path="/:lang/resources/" element={<MainLayout />}>
          <Route index element={<Resources />} />
        </Route>
        <Route path="/:lang/reviews/" element={<MainLayout />}>
          <Route index element={<ProstayReview />} />
        </Route>

        {/* Same pages with language */}
        <Route path="/:lang/hotel-management-software/" element={<MainLayout />}>
          <Route index element={<HotelManagementSoftware />} />
        </Route>
        <Route path="/:lang/multi-property-groups/" element={<MainLayout />}>
          <Route index element={<MultiPropertyGroups />} />
        </Route>
        <Route path="/:lang/vacation-rentals/" element={<MainLayout />}>
          <Route index element={<VacationRentals />} />
        </Route>
        <Route path="/:lang/bed-breakfast/" element={<MainLayout />}>
          <Route index element={<BedAndBreakfast />} />
        </Route>
        <Route path="/:lang/privacy-policy/" element={<MainLayout />}>
          <Route index element={<PrivacyPolicy />} />
        </Route>
        <Route path="/:lang/data-privacy/" element={<MainLayout />}>
          <Route index element={<DataPrivacy />} />
        </Route>
        <Route path="/:lang/cookies-policy/" element={<MainLayout />}>
          <Route index element={<CookiesPolicy />} />
        </Route>
        <Route path="/:lang/small-hotels/" element={<MainLayout />}>
          <Route index element={<SmallHotels />} />
        </Route>
        <Route path="/:lang/terms-and-conditions/" element={<MainLayout />}>
          <Route index element={<TermsAndConditions />} />
        </Route>
        <Route path="/:lang/support/" element={<MainLayout />}>
          <Route index element={<ContactSupport />} />
        </Route>
        <Route path="/tools/" element={<MainLayout />}>
          <Route index element={<ToolsDashboard />} />
        </Route>
        <Route path="/linen-par-calculator/" element={<MainLayout />}>
          <Route index element={<LinenParCalculator />} />
        </Route>
        <Route path="/:lang/tools/" element={<MainLayout />}>
          <Route index element={<ToolsDashboard />} />
        </Route>
        <Route path="/adr-calculator/" element={<MainLayout />}>
          <Route index element={<AdrCalculator />} />
        </Route>
        <Route path="/:lang/adr-calculator/" element={<MainLayout />}>
          <Route index element={<AdrCalculator />} />
        </Route>
        <Route path="/:lang/linen-par-calculator/" element={<MainLayout />}>
          <Route index element={<LinenParCalculator />} />
        </Route>
        <Route path="/revpar-calculator/" element={<MainLayout />}>
          <Route index element={<RevPARCalculator />} />
        </Route>
        <Route path="/:lang/revpar-calculator/" element={<MainLayout />}>
          <Route index element={<RevPARCalculator />} />
        </Route>
        <Route path="/occupancy-rate/" element={<MainLayout />}>
          <Route index element={<OccupancyRate />} />
        </Route>
        <Route path="/:lang/occupancy-rate/" element={<MainLayout />}>
          <Route index element={<OccupancyRate />} />
        </Route>
        <Route path="/goppar-calculator/" element={<MainLayout />}>
          <Route index element={<GopparCalculator />} />
        </Route>
        <Route path="/:lang/goppar-calculator/" element={<MainLayout />}>
          <Route index element={<GopparCalculator />} />
        </Route>
        <Route path="/trevpar-calculator/" element={<MainLayout />}>
          <Route index element={<TRevPARCalculator />} />
        </Route>
        <Route path="/:lang/trevpar-calculator/" element={<MainLayout />}>
          <Route index element={<TRevPARCalculator />} />
        </Route>
        <Route path="/trevpab-calculator/" element={<MainLayout />}>
          <Route index element={<TRevPABCalculator />} />
        </Route>
        <Route path="/:lang/trevpab-calculator/" element={<MainLayout />}>
          <Route index element={<TRevPABCalculator />} />
        </Route>
        <Route path="/cpor-calculator/" element={<MainLayout />}>
          <Route index element={<CPORCalculator />} />
        </Route>
        <Route path="/:lang/cpor-calculator/" element={<MainLayout />}>
          <Route index element={<CPORCalculator />} />
        </Route>
        <Route path="/alos-calculator/" element={<MainLayout />}>
          <Route index element={<ALOSCalculator />} />
        </Route>
        <Route path="/:lang/alos-calculator/" element={<MainLayout />}>
          <Route index element={<ALOSCalculator />} />
        </Route>
        <Route path="/:lang/contact-us/" element={<MainLayout />}>
          <Route index element={<ContactUs />} />
        </Route>
        <Route path="/compare/" element={<MainLayout />}>
          <Route index element={<ProstayAlternatives />} />
        </Route>
        <Route path="/:lang/compare/" element={<MainLayout />}>
          <Route index element={<ProstayAlternatives />} />
        </Route>
        <Route path="/compare/prostay-vs-cloudbeds/" element={<MainLayout />}>
          <Route index element={<ProstayCloudbeds />} />
        </Route>
        <Route path="/:lang/compare/prostay-vs-cloudbeds/" element={<MainLayout />}>
          <Route index element={<ProstayCloudbeds />} />
        </Route>
        <Route path="/compare/prostay-vs-mews/" element={<MainLayout />}>
          <Route index element={<ProstayMews />} />
        </Route>
        <Route path="/:lang/compare/prostay-vs-mews/" element={<MainLayout />}>
          <Route index element={<ProstayMews />} />
        </Route>
        <Route path="/compare/prostay-vs-roomraccoon/" element={<MainLayout />}>
          <Route index element={<ProstayRoomRaccoon />} />
        </Route>
        <Route path="/:lang/compare/prostay-vs-roomraccoon/" element={<MainLayout />}>
          <Route index element={<ProstayRoomRaccoon />} />
        </Route>
        <Route path="/compare/prostay-vs-protel/" element={<MainLayout />}>
          <Route index element={<ProstayProtel />} />
        </Route>
        <Route path="/:lang/compare/prostay-vs-protel/" element={<MainLayout />}>
          <Route index element={<ProstayProtel />} />
        </Route>
        <Route path="/compare/prostay-vs-littlehotelier/" element={<MainLayout />}>
          <Route index element={<ProstayLittlehotelier />} />
        </Route>
        <Route path="/:lang/compare/prostay-vs-littlehotelier/" element={<MainLayout />}>
          <Route index element={<ProstayLittlehotelier />} />
        </Route>
        <Route path="/customer-acquisition-cost-cac-calculator/" element={<MainLayout />}>
          <Route index element={<CustomerAcquisitionCost />} />
        </Route>
        <Route path="/:lang/customer-acquisition-cost-cac-calculator/" element={<MainLayout />}>
          <Route index element={<CustomerAcquisitionCost />} />
        </Route>
        {/*
        <Route path="/prostay-vs-opera/" element={<MainLayout />}>
          <Route index element={<ProstayOpera />} />
        </Route>
        <Route path="/:lang/prostay-vs-opera/" element={<MainLayout />}>
          <Route index element={<ProstayOpera />} />
        </Route>
       */}
        {/* Product pages with language */}
        <Route path="/:lang/property-management-system/" element={<MainLayout />}>
          <Route index element={<LanguageGuard><PropertyManagementSystem /></LanguageGuard>} />
        </Route>
        <Route path="/:lang/channel-manager/" element={<MainLayout />}>
          <Route index element={<ChannelManager />} />
        </Route>
        <Route path="/:lang/booking-engine/" element={<MainLayout />}>
          <Route index element={<BookingEngine />} />
        </Route>
        <Route path="/:lang/ai/" element={<MainLayout />}>
          <Route index element={<ProstayAi />} />
        </Route>
        <Route path="/:lang/ai/ai-booking-assistant/" element={<MainLayout />}>
          <Route index element={<AiReservationsAgent />} />
        </Route>
        <Route path="/:lang/ai/ai-front-desk-agent/" element={<MainLayout />}>
          <Route index element={<AiFrontOffice />} />
        </Route>
        <Route path="/:lang/point-of-sale/" element={<MainLayout />}>
          <Route index element={<PointOfSale />} />
        </Route>
        <Route path="/:lang/prostay-nexus/" element={<MainLayout />}>
          <Route index element={<ProstayNexus />} />
        </Route>
        <Route path="/:lang/prostay-pay/" element={<MainLayout />}>
          <Route index element={<ProstayPay />} />
        </Route>
        <Route path="/:lang/resources/:categoryName/:subCategoryName/:slug/" element={<MainLayout />}>
          <Route index element={<Article />} />
        </Route>
        <Route path="/:lang/resources/:categoryName/" element={<MainLayout />}>
          <Route index element={<Category />} />
        </Route>
        <Route path="/:lang/resources/:categoryName/:subCategoryName/" element={<MainLayout />}>
          <Route index element={<Category />} />
        </Route>
        <Route path="/:lang/blog/:slug/" element={<MainLayout />}>
          <Route index element={<BlogPage />} />
        </Route>
        <Route path="/:lang/blog/:slug/page/:pageNum" element={<MainLayout />}>
          <Route index element={<BlogPage />} />
        </Route>
        <Route path="/hotel-sales-software/" element={<MainLayout />}>
          <Route index element={<HotelSales />} />
        </Route>
        <Route path="/:lang/hotel-sales/" element={<MainLayout />}>
          <Route index element={<HotelSales />} />
        </Route>
        <Route path="/:lang/not-found/" element={<MainLayout />}>
          <Route index element={<NotFound />} />
        </Route>
        <Route path="*" element={<NotFound />} />

      </Routes>
    </Router>
  );
}

export default App;
